import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Tabs,
  Tab,
  Grid,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon, Save as SaveIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import type { Trabajador, CreateTrabajadorRequest, TrabajadorConcepto, ConceptoPlanilla } from '../types/trabajador';
import type { DireccionTrabajador } from '../types/direccion.types';
import { initialDireccion, TIPOS_VIA, TIPOS_ZONA } from '../types/direccion.types';
import type { Dependiente } from '../types/dependiente.types';
import { initialDependiente, PARENTESCOS } from '../types/dependiente.types';
import { DireccionService } from '../services/direccionService';
import { DependienteService } from '../services/dependienteService';
import { CuentaBancariaService, initialCuentaBancaria } from '../services/cuentaBancariaService';
import type { CuentaBancariaTrabajador } from '../services/cuentaBancariaService';
import { TrabajadorService } from '../services/trabajadorService';
import { useTrabajadorStore } from '../store';

interface EditTrabajadorProps {
  open: boolean;
  trabajador: Trabajador | null;
  onClose: () => void;
}

const TIPOS_IDENTIFICACION = [
  { id: 2, descripcion: 'DNI' },
  { id: 3, descripcion: 'Pasaporte' },
  { id: 7, descripcion: 'Carne Extranjeria' },
];

const GENEROS = [
  { id: 7, descripcion: 'Masculino' },
  { id: 3, descripcion: 'Femenino' },
];

const emptyForm: CreateTrabajadorRequest = {
  idTrabajador: 0,
  identificacion: '',
  nombreCompleto: '',
  idTipoIdentificacion: 2,
  idGenero: 7,
  fechaNacimiento: null,
  telefono: null,
  correo: null,
  idRegimenLaboral: 0,
  idModalidadContrato: 0,
  idSituacion: 1,
  idCategoriaTrabajador: 1,
  fechaIngreso: new Date().toISOString().split('T')[0],
  fechaCese: null,
  idMotivoCese: null,
  idRegimenPensionario: 0,
  codigoCUSSP: null,
  fechaAfiliacionAFP: null,
  idArea: null,
  codigoSituacionSUNAT: null,
  esRegimenMicroempresa: false,
  tieneConvenioDobleImposicion: false,
  tieneHijos: false,
};

function trabajadorToForm(t: Trabajador): CreateTrabajadorRequest {
  return {
    idTrabajador: t.idTrabajador,
    identificacion: t.personal?.identificacion || '',
    nombreCompleto: t.personal?.personaContacto || '',
    idTipoIdentificacion: t.personal?.idTipoIdentificacion || 2,
    idGenero: t.personal?.idGenero || 7,
    fechaNacimiento: t.personal?.fechaNacimientoContacto?.split('T')[0] || null,
    telefono: t.personal?.telefonoContacto || null,
    correo: t.personal?.correoContacto || null,
    idRegimenLaboral: t.idRegimenLaboral,
    idModalidadContrato: t.idModalidadContrato,
    idSituacion: t.idSituacion,
    idCategoriaTrabajador: t.idCategoriaTrabajador,
    fechaIngreso: t.fechaIngreso,
    fechaCese: t.fechaCese,
    idMotivoCese: t.idMotivoCese,
    idRegimenPensionario: t.idRegimenPensionario,
    codigoCUSSP: t.codigoCUSSP,
    fechaAfiliacionAFP: t.fechaAfiliacionAFP,
    idArea: t.idArea,
    codigoSituacionSUNAT: t.codigoSituacionSUNAT,
    esRegimenMicroempresa: t.esRegimenMicroempresa,
    tieneConvenioDobleImposicion: t.tieneConvenioDobleImposicion,
    tieneHijos: t.tieneHijos,
  };
}

const EditTrabajador: React.FC<EditTrabajadorProps> = ({ open, trabajador, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    regimenesLaborales,
    modalidadesContrato,
    situaciones,
    categorias,
    motivosCese,
    areas,
    regimenesPensionarios,
    saveTrabajador,
    loading,
    error: storeError,
    clearError,
  } = useTrabajadorStore();

  const [formData, setFormData] = useState<CreateTrabajadorRequest>(emptyForm);
  const [tabValue, setTabValue] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  // Direcciones state
  const [direcciones, setDirecciones] = useState<DireccionTrabajador[]>([]);
  const [direccionForm, setDireccionForm] = useState<DireccionTrabajador>(initialDireccion);
  const [direccionLoading, setDireccionLoading] = useState(false);
  const [showDireccionForm, setShowDireccionForm] = useState(false);

  // Dependientes state
  const [dependientes, setDependientes] = useState<Dependiente[]>([]);
  const [dependienteForm, setDependienteForm] = useState<Dependiente>(initialDependiente);
  const [dependienteLoading, setDependienteLoading] = useState(false);
  const [showDependienteForm, setShowDependienteForm] = useState(false);

  // Cuentas Bancarias state
  const [cuentas, setCuentas] = useState<CuentaBancariaTrabajador[]>([]);
  const [cuentaForm, setCuentaForm] = useState<CuentaBancariaTrabajador>(initialCuentaBancaria);
  const [cuentaLoading, setCuentaLoading] = useState(false);
  const [showCuentaForm, setShowCuentaForm] = useState(false);

  // Conceptos state
  const [conceptos, setConceptos] = useState<TrabajadorConcepto[]>([]);
  const [conceptosDisponibles, setConceptosDisponibles] = useState<ConceptoPlanilla[]>([]);
  const [conceptoLoading, setConceptoLoading] = useState(false);
  const [showConceptoForm, setShowConceptoForm] = useState(false);
  const [conceptoForm, setConceptoForm] = useState<TrabajadorConcepto>({
    idTrabajadorConcepto: 0, idTrabajador: 0, idConcepto: 0,
    montoFijo: 0, porcentaje: null, vigenciaDesde: null, vigenciaHasta: null, activo: true,
  });

  useEffect(() => {
    if (open) {
      setFormData(trabajador ? trabajadorToForm(trabajador) : emptyForm);
      setTabValue(0);
      setErrors({});
      setSaveError(null);
      clearError();
      setDirecciones([]);
      setDependientes([]);
      setConceptos([]);
      setCuentas([]);
      setShowDireccionForm(false);
      setShowDependienteForm(false);
      setShowConceptoForm(false);
      setShowCuentaForm(false);
      setDireccionForm(initialDireccion);
      setDependienteForm(initialDependiente);
      setCuentaForm(initialCuentaBancaria);
      setConceptoForm({ idTrabajadorConcepto: 0, idTrabajador: 0, idConcepto: 0, montoFijo: 0, porcentaje: null, vigenciaDesde: null, vigenciaHasta: null, activo: true });
    }
  }, [open, trabajador, clearError]);

  // Load conceptos when tab 3 is selected
  useEffect(() => {
    if (tabValue === 3 && trabajador && trabajador.idTrabajador > 0) {
      loadConceptos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, trabajador]);

  // Load direcciones when tab 4 is selected
  useEffect(() => {
    if (tabValue === 4 && trabajador && trabajador.idTrabajador > 0) {
      loadDirecciones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, trabajador]);

  // Load dependientes when tab 5 is selected
  useEffect(() => {
    if (tabValue === 5 && trabajador && trabajador.idTrabajador > 0) {
      loadDependientes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, trabajador]);

  // Load cuentas bancarias when tab 6 is selected
  useEffect(() => {
    if (tabValue === 6 && trabajador && trabajador.idTrabajador > 0) {
      loadCuentas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, trabajador]);

  const loadConceptos = async () => {
    if (!trabajador) return;
    setConceptoLoading(true);
    try {
      const [asignados, disponibles] = await Promise.all([
        TrabajadorService.getConceptos(trabajador.idTrabajador),
        TrabajadorService.getConceptosPlanilla(),
      ]);
      setConceptos(asignados);
      setConceptosDisponibles(disponibles.filter((c) => c.activo));
    } catch {
      // handled by errorService
    } finally {
      setConceptoLoading(false);
    }
  };

  const loadDirecciones = async () => {
    if (!trabajador) return;
    setDireccionLoading(true);
    try {
      const data = await DireccionService.getByTrabajador(trabajador.idTrabajador);
      setDirecciones(data);
    } catch {
      // handled by errorService
    } finally {
      setDireccionLoading(false);
    }
  };

  const loadDependientes = async () => {
    if (!trabajador) return;
    setDependienteLoading(true);
    try {
      const data = await DependienteService.getByTrabajador(trabajador.idTrabajador);
      setDependientes(data);
    } catch {
      // handled by errorService
    } finally {
      setDependienteLoading(false);
    }
  };

  const loadCuentas = async () => {
    if (!trabajador) return;
    setCuentaLoading(true);
    try {
      const data = await CuentaBancariaService.getByTrabajador(trabajador.idTrabajador);
      setCuentas(data);
    } catch {
      // handled by errorService
    } finally {
      setCuentaLoading(false);
    }
  };

  const handleChange = (field: keyof CreateTrabajadorRequest, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
    setSaveError(null);
  };

  const handleSelectChange = (field: keyof CreateTrabajadorRequest, value: unknown) => {
    const numValue = value === '' ? 0 : Number(value);
    handleChange(field, numValue);
  };

  const handleNullableSelectChange = (field: keyof CreateTrabajadorRequest, value: unknown) => {
    const numValue = value === '' ? null : Number(value);
    handleChange(field, numValue);
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.identificacion?.trim()) newErrors.identificacion = 'Identificacion es requerida';
    if (!formData.nombreCompleto?.trim()) newErrors.nombreCompleto = 'Nombre completo es requerido';
    if (!formData.idRegimenLaboral) newErrors.idRegimenLaboral = 'Regimen laboral es requerido';
    if (!formData.idModalidadContrato) newErrors.idModalidadContrato = 'Modalidad es requerida';
    if (!formData.idRegimenPensionario) newErrors.idRegimenPensionario = 'Regimen pensionario es requerido';
    if (!formData.fechaIngreso) newErrors.fechaIngreso = 'Fecha de ingreso es requerida';
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const tab0Fields = ['identificacion', 'nombreCompleto'];
      const tab1Fields = ['idRegimenLaboral', 'idModalidadContrato', 'idRegimenPensionario', 'fechaIngreso'];
      const errorFields = Object.keys(newErrors);

      if (errorFields.some(f => tab0Fields.includes(f))) {
        setTabValue(0);
      } else if (errorFields.some(f => tab1Fields.includes(f))) {
        setTabValue(1);
      }

      const errorMessages = Object.values(newErrors).join(', ');
      setSaveError(`Campos requeridos: ${errorMessages}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      await saveTrabajador(formData);
      onClose();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al guardar trabajador';
      setSaveError(message);
    }
  };

  // --- Conceptos handlers ---
  const handleConceptoFormChange = (field: keyof TrabajadorConcepto, value: unknown) => {
    setConceptoForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveConcepto = async () => {
    if (!trabajador || !conceptoForm.idConcepto) return;
    try {
      const toSave = { ...conceptoForm, idTrabajador: trabajador.idTrabajador };
      await TrabajadorService.saveConcepto(trabajador.idTrabajador, toSave);
      setShowConceptoForm(false);
      setConceptoForm({ idTrabajadorConcepto: 0, idTrabajador: trabajador.idTrabajador, idConcepto: 0, montoFijo: 0, porcentaje: null, vigenciaDesde: null, vigenciaHasta: null, activo: true });
      await loadConceptos();
    } catch {
      // handled by errorService
    }
  };

  const handleDeleteConcepto = async (idTC: number) => {
    if (!trabajador) return;
    try {
      await TrabajadorService.deleteConcepto(trabajador.idTrabajador, idTC);
      await loadConceptos();
    } catch {
      // handled by errorService
    }
  };

  const handleEditConcepto = (tc: TrabajadorConcepto) => {
    setConceptoForm(tc);
    setShowConceptoForm(true);
  };

  // --- Direcciones handlers ---
  const handleDireccionFormChange = (field: keyof DireccionTrabajador, value: unknown) => {
    setDireccionForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDireccion = async () => {
    if (!trabajador) return;
    try {
      const toSave = { ...direccionForm, idTrabajador: trabajador.idTrabajador };
      await DireccionService.save(toSave);
      setShowDireccionForm(false);
      setDireccionForm({ ...initialDireccion, idTrabajador: trabajador.idTrabajador });
      await loadDirecciones();
    } catch {
      // handled by errorService
    }
  };

  const handleDeleteDireccion = async (id: number) => {
    try {
      await DireccionService.delete(id);
      await loadDirecciones();
    } catch {
      // handled by errorService
    }
  };

  const handleEditDireccion = (dir: DireccionTrabajador) => {
    setDireccionForm(dir);
    setShowDireccionForm(true);
  };

  // --- Dependientes handlers ---
  const handleDependienteFormChange = (field: keyof Dependiente, value: unknown) => {
    setDependienteForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveDependiente = async () => {
    if (!trabajador) return;
    try {
      const toSave = { ...dependienteForm, idTrabajador: trabajador.idTrabajador };
      await DependienteService.save(toSave);
      setShowDependienteForm(false);
      setDependienteForm({ ...initialDependiente, idTrabajador: trabajador.idTrabajador });
      await loadDependientes();
    } catch {
      // handled by errorService
    }
  };

  const handleDeleteDependiente = async (id: number) => {
    try {
      await DependienteService.delete(id);
      await loadDependientes();
    } catch {
      // handled by errorService
    }
  };

  const handleEditDependiente = (dep: Dependiente) => {
    setDependienteForm(dep);
    setShowDependienteForm(true);
  };

  // --- Cuentas Bancarias handlers ---
  const handleCuentaFormChange = (field: keyof CuentaBancariaTrabajador, value: unknown) => {
    setCuentaForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCuenta = async () => {
    if (!trabajador || !cuentaForm.nombreBanco || !cuentaForm.numeroCuenta) return;
    try {
      const toSave = { ...cuentaForm, idTrabajador: trabajador.idTrabajador };
      await CuentaBancariaService.save(toSave);
      setShowCuentaForm(false);
      setCuentaForm({ ...initialCuentaBancaria, idTrabajador: trabajador.idTrabajador });
      await loadCuentas();
    } catch {
      // handled by errorService
    }
  };

  const handleDeleteCuenta = async (id: number) => {
    try {
      await CuentaBancariaService.delete(id);
      await loadCuentas();
    } catch {
      // handled by errorService
    }
  };

  const handleEditCuenta = (cuenta: CuentaBancariaTrabajador) => {
    setCuentaForm(cuenta);
    setShowCuentaForm(true);
  };

  // --- DataGrid columns ---
  const conceptoColumns: GridColDef[] = [
    {
      field: 'codigo',
      headerName: 'Codigo',
      width: 80,
      valueGetter: (_value: unknown, row: TrabajadorConcepto) => row.concepto?.codigo || '',
    },
    {
      field: 'descripcion',
      headerName: 'Concepto',
      flex: 1,
      minWidth: 180,
      valueGetter: (_value: unknown, row: TrabajadorConcepto) => row.concepto?.descripcion || '',
    },
    {
      field: 'tipoConcepto',
      headerName: 'Tipo',
      width: 70,
      valueGetter: (_value: unknown, row: TrabajadorConcepto) => {
        const t = row.concepto?.tipoConcepto;
        return t === 'R' ? 'Rem.' : t === 'D' ? 'Desc.' : t === 'A' ? 'Aporte' : t || '';
      },
    },
    {
      field: 'montoFijo',
      headerName: 'Monto Fijo',
      width: 110,
      type: 'number',
      valueFormatter: (value: number) => value ? `S/ ${value.toFixed(2)}` : '-',
    },
    {
      field: 'porcentaje',
      headerName: '%',
      width: 70,
      type: 'number',
      valueFormatter: (value: number | null) => value != null ? `${value}%` : '-',
    },
    {
      field: 'activo',
      headerName: 'Activo',
      width: 70,
      valueGetter: (_value: unknown, row: TrabajadorConcepto) => row.activo ? 'Si' : 'No',
    },
    {
      field: 'acciones',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Eliminar">
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteConcepto(params.row.idTrabajadorConcepto); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const direccionColumns: GridColDef[] = [
    { field: 'tipoDireccion', headerName: 'Tipo', width: 110 },
    {
      field: 'direccion',
      headerName: 'Direccion',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value: unknown, row: DireccionTrabajador) =>
        [row.tipoVia, row.nombreVia, row.numeroVia].filter(Boolean).join(' '),
    },
    {
      field: 'zona',
      headerName: 'Zona',
      width: 160,
      valueGetter: (_value: unknown, row: DireccionTrabajador) =>
        [row.tipoZona, row.nombreZona].filter(Boolean).join(' '),
    },
    { field: 'ubigeo', headerName: 'Ubigeo', width: 80 },
    { field: 'departamentoGeo', headerName: 'Departamento', width: 120 },
    { field: 'provinciaGeo', headerName: 'Provincia', width: 110 },
    { field: 'distritoGeo', headerName: 'Distrito', width: 110 },
    {
      field: 'acciones',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Eliminar">
          <IconButton size="small" color="error" onClick={() => handleDeleteDireccion(params.row.idDireccion)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const dependienteColumns: GridColDef[] = [
    { field: 'parentesco', headerName: 'Parentesco', width: 110 },
    { field: 'numeroDocumento', headerName: 'Nro. Doc.', width: 110 },
    {
      field: 'nombreCompleto',
      headerName: 'Nombres',
      flex: 1,
      minWidth: 200,
      valueGetter: (_value: unknown, row: Dependiente) =>
        [row.apellidoPaterno, row.apellidoMaterno, row.nombres].filter(Boolean).join(' '),
    },
    {
      field: 'fechaNacimiento',
      headerName: 'Fec. Nacimiento',
      width: 120,
      valueGetter: (_value: unknown, row: Dependiente) =>
        row.fechaNacimiento ? row.fechaNacimiento.split('T')[0] : '',
    },
    { field: 'sexo', headerName: 'Sexo', width: 60 },
    {
      field: 'esMenorEdad',
      headerName: 'Menor',
      width: 70,
      valueGetter: (_value: unknown, row: Dependiente) => (row.esMenorEdad ? 'Si' : 'No'),
    },
    {
      field: 'esEstudiante',
      headerName: 'Estudiante',
      width: 90,
      valueGetter: (_value: unknown, row: Dependiente) => (row.esEstudiante ? 'Si' : 'No'),
    },
    {
      field: 'tieneDiscapacidad',
      headerName: 'Discapacidad',
      width: 100,
      valueGetter: (_value: unknown, row: Dependiente) => (row.tieneDiscapacidad ? 'Si' : 'No'),
    },
    {
      field: 'acciones',
      headerName: '',
      width: 60,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Eliminar">
          <IconButton size="small" color="error" onClick={() => handleDeleteDependiente(params.row.idDependiente)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const cuentaColumns: GridColDef[] = [
    { field: 'nombreBanco', headerName: 'Banco', flex: 1, minWidth: 140 },
    { field: 'numeroCuenta', headerName: 'N° Cuenta', width: 160 },
    { field: 'cci', headerName: 'CCI', width: 160, valueGetter: (_v: unknown, row: CuentaBancariaTrabajador) => row.cci || '-' },
    { field: 'codigoOficina', headerName: 'Cod. Oficina', width: 100, valueGetter: (_v: unknown, row: CuentaBancariaTrabajador) => row.codigoOficina || '-' },
    {
      field: 'tipoCuenta', headerName: 'Tipo', width: 100,
      valueGetter: (_v: unknown, row: CuentaBancariaTrabajador) =>
        row.tipoCuenta === 'HABERES' ? 'Haberes' : row.tipoCuenta === 'CTS' ? 'CTS' : 'Otros',
    },
    {
      field: 'esPrincipal', headerName: 'Principal', width: 80,
      valueGetter: (_v: unknown, row: CuentaBancariaTrabajador) => row.esPrincipal ? 'Si' : 'No',
    },
    {
      field: 'acciones', headerName: '', width: 60, sortable: false, filterable: false,
      renderCell: (params) => (
        <Tooltip title="Eliminar">
          <IconButton size="small" color="error" onClick={() => handleDeleteCuenta(params.row.idCuentaBancaria)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const isEdit = !!trabajador?.idTrabajador;
  const displayError = saveError || storeError;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>{isEdit ? 'Editar Trabajador' : 'Nuevo Trabajador'}</DialogTitle>
      <DialogContent>
        {displayError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setSaveError(null); clearError(); }}>
            {displayError}
          </Alert>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={tabValue} onChange={(_e, val) => setTabValue(val)} variant="scrollable" scrollButtons="auto">
            <Tab label="Datos Personales" />
            <Tab label="Datos Laborales" />
            <Tab label="Otros" />
            <Tab label="Conceptos" disabled={!isEdit} />
            <Tab label="Direcciones" disabled={!isEdit} />
            <Tab label="Dependientes" disabled={!isEdit} />
            <Tab label="Ctas. Bancarias" disabled={!isEdit} />
          </Tabs>
        </Box>

        {/* Tab 0: Datos Personales */}
        {tabValue === 0 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Tipo Identificacion</InputLabel>
                <Select
                  value={formData.idTipoIdentificacion || ''}
                  label="Tipo Identificacion"
                  onChange={(e) => handleSelectChange('idTipoIdentificacion', e.target.value)}
                >
                  {TIPOS_IDENTIFICACION.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Nro. Identificacion"
                fullWidth
                size="small"
                value={formData.identificacion}
                onChange={(e) => handleChange('identificacion', e.target.value)}
                error={!!errors.identificacion}
                helperText={errors.identificacion}
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                label="Nombre Completo"
                fullWidth
                size="small"
                value={formData.nombreCompleto}
                onChange={(e) => handleChange('nombreCompleto', e.target.value)}
                error={!!errors.nombreCompleto}
                helperText={errors.nombreCompleto}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Genero</InputLabel>
                <Select
                  value={formData.idGenero || ''}
                  label="Genero"
                  onChange={(e) => handleSelectChange('idGenero', e.target.value)}
                >
                  {GENEROS.map((g) => (
                    <MenuItem key={g.id} value={g.id}>
                      {g.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Fecha Nacimiento"
                value={formData.fechaNacimiento ? new Date(formData.fechaNacimiento + 'T00:00:00') : null}
                onChange={(date: Date | null) => {
                  if (date && !isNaN(date.getTime())) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    handleChange('fechaNacimiento', `${y}-${m}-${d}`);
                  } else {
                    handleChange('fechaNacimiento', null);
                  }
                }}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Telefono"
                fullWidth
                size="small"
                value={formData.telefono || ''}
                onChange={(e) => handleChange('telefono', e.target.value || null)}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Correo"
                fullWidth
                size="small"
                value={formData.correo || ''}
                onChange={(e) => handleChange('correo', e.target.value || null)}
                inputProps={{ maxLength: 100 }}
              />
            </Grid>
          </Grid>
        )}

        {/* Tab 1: Datos Laborales */}
        {tabValue === 1 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Fecha Ingreso"
                value={formData.fechaIngreso ? new Date(formData.fechaIngreso.split('T')[0] + 'T00:00:00') : null}
                onChange={(date: Date | null) => {
                  if (date && !isNaN(date.getTime())) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    handleChange('fechaIngreso', `${y}-${m}-${d}`);
                  } else {
                    handleChange('fechaIngreso', '');
                  }
                }}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true, size: 'small', error: !!errors.fechaIngreso, helperText: errors.fechaIngreso } }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" error={!!errors.idRegimenLaboral}>
                <InputLabel>Regimen Laboral</InputLabel>
                <Select
                  value={formData.idRegimenLaboral || ''}
                  label="Regimen Laboral"
                  onChange={(e) => handleSelectChange('idRegimenLaboral', e.target.value)}
                >
                  {regimenesLaborales.map((r) => (
                    <MenuItem key={r.idRegimenLaboral} value={r.idRegimenLaboral}>
                      {r.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" error={!!errors.idModalidadContrato}>
                <InputLabel>Modalidad Contrato</InputLabel>
                <Select
                  value={formData.idModalidadContrato || ''}
                  label="Modalidad Contrato"
                  onChange={(e) => handleSelectChange('idModalidadContrato', e.target.value)}
                >
                  {modalidadesContrato.map((m) => (
                    <MenuItem key={m.idModalidadContrato} value={m.idModalidadContrato}>
                      {m.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Situacion</InputLabel>
                <Select
                  value={formData.idSituacion || ''}
                  label="Situacion"
                  onChange={(e) => handleSelectChange('idSituacion', e.target.value)}
                >
                  {situaciones.map((s) => (
                    <MenuItem key={s.idSituacion} value={s.idSituacion}>
                      {s.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Categoria</InputLabel>
                <Select
                  value={formData.idCategoriaTrabajador || ''}
                  label="Categoria"
                  onChange={(e) => handleSelectChange('idCategoriaTrabajador', e.target.value)}
                >
                  {categorias.map((c) => (
                    <MenuItem key={c.idCategoriaTrabajador} value={c.idCategoriaTrabajador}>
                      {c.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small" error={!!errors.idRegimenPensionario}>
                <InputLabel>Regimen Pensionario</InputLabel>
                <Select
                  value={formData.idRegimenPensionario || ''}
                  label="Regimen Pensionario"
                  onChange={(e) => handleSelectChange('idRegimenPensionario', e.target.value)}
                >
                  {regimenesPensionarios.map((r) => (
                    <MenuItem key={r.idRegimenPensionario} value={r.idRegimenPensionario}>
                      {r.descripcion} ({r.tipo})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Area</InputLabel>
                <Select
                  value={formData.idArea || ''}
                  label="Area"
                  onChange={(e) => handleNullableSelectChange('idArea', e.target.value)}
                >
                  <MenuItem value="">Sin area</MenuItem>
                  {areas.map((a) => (
                    <MenuItem key={a.idArea} value={a.idArea}>
                      {a.descripcion}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Fecha Cese"
                value={formData.fechaCese ? new Date(formData.fechaCese.split('T')[0] + 'T00:00:00') : null}
                onChange={(date: Date | null) => {
                  if (date && !isNaN(date.getTime())) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    handleChange('fechaCese', `${y}-${m}-${d}`);
                  } else {
                    handleChange('fechaCese', null);
                  }
                }}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            {formData.fechaCese && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Motivo Cese</InputLabel>
                  <Select
                    value={formData.idMotivoCese || ''}
                    label="Motivo Cese"
                    onChange={(e) => handleNullableSelectChange('idMotivoCese', e.target.value)}
                  >
                    {motivosCese.map((m) => (
                      <MenuItem key={m.idMotivoCese} value={m.idMotivoCese}>
                        {m.descripcion}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        )}

        {/* Tab 2: Otros */}
        {tabValue === 2 && (
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Pensiones
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Codigo CUSSP"
                fullWidth
                size="small"
                value={formData.codigoCUSSP || ''}
                onChange={(e) => handleChange('codigoCUSSP', e.target.value || null)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <DatePicker
                label="Fecha Afiliacion AFP"
                value={formData.fechaAfiliacionAFP ? new Date(formData.fechaAfiliacionAFP.split('T')[0] + 'T00:00:00') : null}
                onChange={(date: Date | null) => {
                  if (date && !isNaN(date.getTime())) {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const d = String(date.getDate()).padStart(2, '0');
                    handleChange('fechaAfiliacionAFP', `${y}-${m}-${d}`);
                  } else {
                    handleChange('fechaAfiliacionAFP', null);
                  }
                }}
                format="dd/MM/yyyy"
                slotProps={{ textField: { fullWidth: true, size: 'small' } }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                Configuracion
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Codigo Situacion SUNAT"
                fullWidth
                size="small"
                value={formData.codigoSituacionSUNAT || ''}
                onChange={(e) => handleChange('codigoSituacionSUNAT', e.target.value || null)}
                inputProps={{ maxLength: 3 }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tieneHijos}
                    onChange={(e) => handleChange('tieneHijos', e.target.checked)}
                  />
                }
                label="Tiene hijos"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.esRegimenMicroempresa}
                    onChange={(e) => handleChange('esRegimenMicroempresa', e.target.checked)}
                  />
                }
                label="Regimen Microempresa"
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.tieneConvenioDobleImposicion}
                    onChange={(e) => handleChange('tieneConvenioDobleImposicion', e.target.checked)}
                  />
                }
                label="Conv. Doble Imposicion"
              />
            </Grid>
          </Grid>
        )}

        {/* Tab 3: Conceptos */}
        {tabValue === 3 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Conceptos Asignados al Trabajador</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setConceptoForm({ idTrabajadorConcepto: 0, idTrabajador: trabajador?.idTrabajador || 0, idConcepto: 0, montoFijo: 0, porcentaje: null, vigenciaDesde: null, vigenciaHasta: null, activo: true });
                  setShowConceptoForm(true);
                }}
              >
                Agregar Concepto
              </Button>
            </Box>

            <DataGrid
              rows={conceptos}
              columns={conceptoColumns}
              getRowId={(row) => row.idTrabajadorConcepto}
              loading={conceptoLoading}
              autoHeight
              pageSizeOptions={[10]}
              initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
              density="compact"
              disableRowSelectionOnClick
              onRowClick={(params) => handleEditConcepto(params.row as TrabajadorConcepto)}
              sx={{ mb: 2, minHeight: 200 }}
            />

            {showConceptoForm && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {conceptoForm.idTrabajadorConcepto > 0 ? 'Editar Concepto' : 'Nuevo Concepto'}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Concepto</InputLabel>
                      <Select
                        value={conceptoForm.idConcepto || ''}
                        label="Concepto"
                        onChange={(e) => handleConceptoFormChange('idConcepto', Number(e.target.value))}
                      >
                        {conceptosDisponibles.map((c) => (
                          <MenuItem key={c.idConcepto} value={c.idConcepto}>
                            {c.codigo} - {c.descripcion} ({c.tipoConcepto === 'R' ? 'Rem.' : c.tipoConcepto === 'D' ? 'Desc.' : 'Aporte'})
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Monto Fijo (S/)"
                      fullWidth
                      size="small"
                      type="number"
                      value={conceptoForm.montoFijo || ''}
                      onChange={(e) => handleConceptoFormChange('montoFijo', Number(e.target.value) || 0)}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Porcentaje (%)"
                      fullWidth
                      size="small"
                      type="number"
                      value={conceptoForm.porcentaje ?? ''}
                      onChange={(e) => handleConceptoFormChange('porcentaje', e.target.value ? Number(e.target.value) : null)}
                      inputProps={{ min: 0, max: 100, step: 0.01 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <DatePicker
                      label="Vigencia Desde"
                      value={conceptoForm.vigenciaDesde ? new Date(conceptoForm.vigenciaDesde.split('T')[0] + 'T00:00:00') : null}
                      onChange={(date: Date | null) => {
                        if (date && !isNaN(date.getTime())) {
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const d = String(date.getDate()).padStart(2, '0');
                          handleConceptoFormChange('vigenciaDesde', `${y}-${m}-${d}`);
                        } else {
                          handleConceptoFormChange('vigenciaDesde', null);
                        }
                      }}
                      format="dd/MM/yyyy"
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <DatePicker
                      label="Vigencia Hasta"
                      value={conceptoForm.vigenciaHasta ? new Date(conceptoForm.vigenciaHasta.split('T')[0] + 'T00:00:00') : null}
                      onChange={(date: Date | null) => {
                        if (date && !isNaN(date.getTime())) {
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const d = String(date.getDate()).padStart(2, '0');
                          handleConceptoFormChange('vigenciaHasta', `${y}-${m}-${d}`);
                        } else {
                          handleConceptoFormChange('vigenciaHasta', null);
                        }
                      }}
                      format="dd/MM/yyyy"
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={conceptoForm.activo}
                          onChange={(e) => handleConceptoFormChange('activo', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Activo"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setShowConceptoForm(false)}>
                        Cancelar
                      </Button>
                      <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveConcepto}>
                        Guardar
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 4: Direcciones */}
        {tabValue === 4 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Direcciones del Trabajador</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setDireccionForm({ ...initialDireccion, idTrabajador: trabajador?.idTrabajador || 0 });
                  setShowDireccionForm(true);
                }}
              >
                Agregar
              </Button>
            </Box>

            <DataGrid
              rows={direcciones}
              columns={direccionColumns}
              getRowId={(row) => row.idDireccion}
              loading={direccionLoading}
              autoHeight
              pageSizeOptions={[5]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              density="compact"
              disableRowSelectionOnClick
              onRowClick={(params) => handleEditDireccion(params.row as DireccionTrabajador)}
              sx={{ mb: 2, minHeight: 200 }}
            />

            {showDireccionForm && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {direccionForm.idDireccion > 0 ? 'Editar Direccion' : 'Nueva Direccion'}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo Direccion</InputLabel>
                      <Select
                        value={direccionForm.tipoDireccion}
                        label="Tipo Direccion"
                        onChange={(e) => handleDireccionFormChange('tipoDireccion', e.target.value)}
                      >
                        <MenuItem value="DOMICILIO">DOMICILIO</MenuItem>
                        <MenuItem value="T_REGISTRO">T_REGISTRO</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo Via</InputLabel>
                      <Select
                        value={direccionForm.tipoVia || ''}
                        label="Tipo Via"
                        onChange={(e) => handleDireccionFormChange('tipoVia', e.target.value || null)}
                      >
                        <MenuItem value="">--</MenuItem>
                        {TIPOS_VIA.map((tv) => (
                          <MenuItem key={tv} value={tv}>{tv}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Nombre Via"
                      fullWidth
                      size="small"
                      value={direccionForm.nombreVia || ''}
                      onChange={(e) => handleDireccionFormChange('nombreVia', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Numero Via"
                      fullWidth
                      size="small"
                      value={direccionForm.numeroVia || ''}
                      onChange={(e) => handleDireccionFormChange('numeroVia', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Interior"
                      fullWidth
                      size="small"
                      value={direccionForm.interior || ''}
                      onChange={(e) => handleDireccionFormChange('interior', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Manzana"
                      fullWidth
                      size="small"
                      value={direccionForm.manzana || ''}
                      onChange={(e) => handleDireccionFormChange('manzana', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }}>
                    <TextField
                      label="Lote"
                      fullWidth
                      size="small"
                      value={direccionForm.lote || ''}
                      onChange={(e) => handleDireccionFormChange('lote', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo Zona</InputLabel>
                      <Select
                        value={direccionForm.tipoZona || ''}
                        label="Tipo Zona"
                        onChange={(e) => handleDireccionFormChange('tipoZona', e.target.value || null)}
                      >
                        <MenuItem value="">--</MenuItem>
                        {TIPOS_ZONA.map((tz) => (
                          <MenuItem key={tz} value={tz}>{tz}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Nombre Zona"
                      fullWidth
                      size="small"
                      value={direccionForm.nombreZona || ''}
                      onChange={(e) => handleDireccionFormChange('nombreZona', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Ubigeo"
                      fullWidth
                      size="small"
                      value={direccionForm.ubigeo || ''}
                      onChange={(e) => handleDireccionFormChange('ubigeo', e.target.value || null)}
                      inputProps={{ maxLength: 6 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Departamento"
                      fullWidth
                      size="small"
                      value={direccionForm.departamentoGeo || ''}
                      onChange={(e) => handleDireccionFormChange('departamentoGeo', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Provincia"
                      fullWidth
                      size="small"
                      value={direccionForm.provinciaGeo || ''}
                      onChange={(e) => handleDireccionFormChange('provinciaGeo', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Distrito"
                      fullWidth
                      size="small"
                      value={direccionForm.distritoGeo || ''}
                      onChange={(e) => handleDireccionFormChange('distritoGeo', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      label="Referencia"
                      fullWidth
                      size="small"
                      value={direccionForm.referencia || ''}
                      onChange={(e) => handleDireccionFormChange('referencia', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setShowDireccionForm(false)}>
                        Cancelar
                      </Button>
                      <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveDireccion}>
                        Guardar Direccion
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}

        {/* Tab 5: Dependientes */}
        {tabValue === 5 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2">Dependientes del Trabajador</Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() => {
                  setDependienteForm({ ...initialDependiente, idTrabajador: trabajador?.idTrabajador || 0 });
                  setShowDependienteForm(true);
                }}
              >
                Agregar
              </Button>
            </Box>

            <DataGrid
              rows={dependientes}
              columns={dependienteColumns}
              getRowId={(row) => row.idDependiente}
              loading={dependienteLoading}
              autoHeight
              pageSizeOptions={[5]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              density="compact"
              disableRowSelectionOnClick
              onRowClick={(params) => handleEditDependiente(params.row as Dependiente)}
              sx={{ mb: 2, minHeight: 200 }}
            />

            {showDependienteForm && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {dependienteForm.idDependiente > 0 ? 'Editar Dependiente' : 'Nuevo Dependiente'}
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Parentesco</InputLabel>
                      <Select
                        value={dependienteForm.parentesco}
                        label="Parentesco"
                        onChange={(e) => handleDependienteFormChange('parentesco', e.target.value)}
                      >
                        {PARENTESCOS.map((p) => (
                          <MenuItem key={p} value={p}>{p}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo Documento</InputLabel>
                      <Select
                        value={dependienteForm.tipoDocumento || ''}
                        label="Tipo Documento"
                        onChange={(e) => handleDependienteFormChange('tipoDocumento', e.target.value || null)}
                      >
                        <MenuItem value="01">DNI</MenuItem>
                        <MenuItem value="04">CE</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Nro. Documento"
                      fullWidth
                      size="small"
                      value={dependienteForm.numeroDocumento || ''}
                      onChange={(e) => handleDependienteFormChange('numeroDocumento', e.target.value || null)}
                      inputProps={{ maxLength: 20 }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Apellido Paterno"
                      fullWidth
                      size="small"
                      value={dependienteForm.apellidoPaterno}
                      onChange={(e) => handleDependienteFormChange('apellidoPaterno', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Apellido Materno"
                      fullWidth
                      size="small"
                      value={dependienteForm.apellidoMaterno || ''}
                      onChange={(e) => handleDependienteFormChange('apellidoMaterno', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <TextField
                      label="Nombres"
                      fullWidth
                      size="small"
                      value={dependienteForm.nombres}
                      onChange={(e) => handleDependienteFormChange('nombres', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <DatePicker
                      label="Fecha Nacimiento"
                      value={dependienteForm.fechaNacimiento ? new Date(dependienteForm.fechaNacimiento.split('T')[0] + 'T00:00:00') : null}
                      onChange={(date: Date | null) => {
                        if (date && !isNaN(date.getTime())) {
                          const y = date.getFullYear();
                          const m = String(date.getMonth() + 1).padStart(2, '0');
                          const d = String(date.getDate()).padStart(2, '0');
                          const fecha = `${y}-${m}-${d}`;
                          handleDependienteFormChange('fechaNacimiento', fecha);
                          const edad = Math.floor((Date.now() - date.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
                          handleDependienteFormChange('esMenorEdad', edad < 18);
                        } else {
                          handleDependienteFormChange('fechaNacimiento', null);
                        }
                      }}
                      format="dd/MM/yyyy"
                      slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sexo</InputLabel>
                      <Select
                        value={dependienteForm.sexo || ''}
                        label="Sexo"
                        onChange={(e) => handleDependienteFormChange('sexo', e.target.value || null)}
                      >
                        <MenuItem value="M">Masculino</MenuItem>
                        <MenuItem value="F">Femenino</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dependienteForm.esMenorEdad}
                          onChange={(e) => handleDependienteFormChange('esMenorEdad', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Menor de Edad"
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dependienteForm.esEstudiante}
                          onChange={(e) => handleDependienteFormChange('esEstudiante', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Estudiante"
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={dependienteForm.tieneDiscapacidad}
                          onChange={(e) => handleDependienteFormChange('tieneDiscapacidad', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Discapacidad"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setShowDependienteForm(false)}>
                        Cancelar
                      </Button>
                      <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveDependiente}>
                        Guardar Dependiente
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
        {/* Tab 6: Cuentas Bancarias */}
        {tabValue === 6 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle2">Cuentas Bancarias del Trabajador</Typography>
              <Button size="small" startIcon={<AddIcon />} onClick={() => { setCuentaForm({ ...initialCuentaBancaria, idTrabajador: trabajador?.idTrabajador || 0 }); setShowCuentaForm(true); }}>
                Agregar Cuenta
              </Button>
            </Box>
            <DataGrid
              rows={cuentas}
              columns={cuentaColumns}
              getRowId={(row) => row.idCuentaBancaria}
              loading={cuentaLoading}
              autoHeight
              pageSizeOptions={[5]}
              initialState={{ pagination: { paginationModel: { pageSize: 5 } } }}
              density="compact"
              disableRowSelectionOnClick
              onRowClick={(params) => handleEditCuenta(params.row as CuentaBancariaTrabajador)}
              sx={{ minHeight: 200 }}
            />
            {showCuentaForm && (
              <Box sx={{ mt: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {cuentaForm.idCuentaBancaria ? 'Editar Cuenta Bancaria' : 'Nueva Cuenta Bancaria'}
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Banco</InputLabel>
                      <Select
                        value={cuentaForm.nombreBanco}
                        label="Banco"
                        onChange={(e) => handleCuentaFormChange('nombreBanco', e.target.value)}
                      >
                        <MenuItem value="BCP">BCP - Banco de Credito</MenuItem>
                        <MenuItem value="BBVA">BBVA Continental</MenuItem>
                        <MenuItem value="Interbank">Interbank</MenuItem>
                        <MenuItem value="Scotiabank">Scotiabank</MenuItem>
                        <MenuItem value="BanBif">BanBif</MenuItem>
                        <MenuItem value="Caja Trujillo">Caja Trujillo</MenuItem>
                        <MenuItem value="Caja Piura">Caja Piura</MenuItem>
                        <MenuItem value="Otro">Otro</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="N° Cuenta Bancaria"
                      fullWidth
                      size="small"
                      value={cuentaForm.numeroCuenta}
                      onChange={(e) => handleCuentaFormChange('numeroCuenta', e.target.value)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="CCI"
                      fullWidth
                      size="small"
                      value={cuentaForm.cci || ''}
                      onChange={(e) => handleCuentaFormChange('cci', e.target.value || null)}
                      placeholder="Codigo Cuenta Interbancario"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <TextField
                      label="Cod. Oficina"
                      fullWidth
                      size="small"
                      value={cuentaForm.codigoOficina || ''}
                      onChange={(e) => handleCuentaFormChange('codigoOficina', e.target.value || null)}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Tipo Cuenta</InputLabel>
                      <Select
                        value={cuentaForm.tipoCuenta}
                        label="Tipo Cuenta"
                        onChange={(e) => handleCuentaFormChange('tipoCuenta', e.target.value)}
                      >
                        <MenuItem value="HABERES">Haberes</MenuItem>
                        <MenuItem value="CTS">CTS</MenuItem>
                        <MenuItem value="OTROS">Otros</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cuentaForm.esPrincipal}
                          onChange={(e) => handleCuentaFormChange('esPrincipal', e.target.checked)}
                          size="small"
                        />
                      }
                      label="Cuenta Principal"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setShowCuentaForm(false)}>
                        Cancelar
                      </Button>
                      <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveCuenta}>
                        Guardar Cuenta
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditTrabajador;
